import React, { lazy, Suspense, useEffect, useRef } from 'react';
import './App.css';
import './mobile-responsive.css';
import './dark-mode.css';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { ThemeProvider } from './context/ThemeContext';
import { SidebarProvider } from './context/SidebarContext';
import { Toaster } from './components/ui/toaster';
import OfflineDetector from './components/OfflineDetector';
import { canSeePage, firstAllowedPage, isAdmin } from './lib/permissions';
import { LoadingIcon } from './components/LoadingSpinner';

// Lazy load all pages for better performance
const Login = lazy(() => import('./pages/Login'));
const Dashboard = lazy(() => import('./pages/Dashboard'));
const Schedule = lazy(() => import('./pages/Schedule'));
const TimeTracking = lazy(() => import('./features/time-tracking/TimeTrackingRefactored'));
const Team = lazy(() => import('./pages/Team'));
const Settings = lazy(() => import('./pages/Settings'));
const CityEditor = lazy(() => import('./pages/CityEditor'));
const AssetLibrary = lazy(() => import('./pages/AssetLibrary'));
const TaskV2 = lazy(() => import('./pages/TaskV2'));
const ScenarioEditor = lazy(() => import('./pages/ScenarioEditor'));
const SocialMedia = lazy(() => import('./pages/SocialMedia'));
const ProductionLine = lazy(() => import('./pages/ProductionLine'));
const ControlPanel = lazy(() => import('./pages/ControlPanel'));// Loading fallback component
const PageLoader = () => (
  <PageLoaderInner />
);

const PageLoaderInner = () => {
  return (
    <div className="flex items-center justify-center min-h-screen bg-[#F8F5EF] dark:bg-[#171112]">
      <LoadingIcon />
    </div>
  );
};

// Entrance animations should feel like a curtain rising — once. After the
// first route renders, later navigations skip the choreography (except the
// login page, which keeps its ceremony).
const MotionGate = () => {
  const location = useLocation();
  const firstRender = useRef(true);
  useEffect(() => {
    if (location.pathname === '/login') {
      document.body.classList.remove('tp-no-entrance');
      firstRender.current = true;
      return;
    }
    if (firstRender.current) {
      firstRender.current = false;
      return;
    }
    document.body.classList.add('tp-no-entrance');
  }, [location.pathname]);
  return null;
};

const ProtectedRoute = ({ children, path }) => {
  const { isAuthenticated, user } = useAuth();
  if (!isAuthenticated) return <Navigate to="/login" />;
  // Page-level access control from the admin Control Panel
  if (path && !canSeePage(user, path)) return <Navigate to={firstAllowedPage(user)} replace />;
  return children;
};

const AdminRoute = ({ children }) => {
  const { isAuthenticated, user } = useAuth();
  if (!isAuthenticated) return <Navigate to="/login" />;
  return isAdmin(user) ? children : <Navigate to={firstAllowedPage(user)} replace />;
};

const PublicRoute = ({ children }) => {
  const { isAuthenticated } = useAuth();
  // Allow access to login page when not authenticated
  // If already authenticated, redirect to dashboard
  return !isAuthenticated ? children : <Navigate to="/dashboard" replace />;
};

function AppRoutes() {
  return (
    <Suspense fallback={<PageLoader />}>
      <MotionGate />
      <Routes>
        <Route path="/login" element={
          <PublicRoute>
            <Login />
          </PublicRoute>
        } />
        <Route path="/dashboard" element={
          <ProtectedRoute path="/dashboard">
            <Dashboard />
          </ProtectedRoute>
        } />
        <Route path="/schedule" element={
          <ProtectedRoute path="/schedule">
            <Schedule />
          </ProtectedRoute>
        } />
        <Route path="/time-tracking" element={
          <ProtectedRoute path="/time-tracking">
            <TimeTracking />
          </ProtectedRoute>
        } />
        <Route path="/tasks" element={
          <ProtectedRoute path="/tasks">
            <Navigate to="/task-v2" replace />
          </ProtectedRoute>
        } />
        <Route path="/team" element={
          <ProtectedRoute path="/team">
            <Team />
          </ProtectedRoute>
        } />
        <Route path="/city-editor" element={
          <ProtectedRoute path="/city-editor">
            <CityEditor />
          </ProtectedRoute>
        } />
        <Route path="/asset-library" element={
          <ProtectedRoute path="/asset-library">
            <AssetLibrary />
          </ProtectedRoute>
        } />
        <Route path="/task-v2" element={
          <ProtectedRoute path="/task-v2">
            <TaskV2 />
          </ProtectedRoute>
        } />
        <Route path="/scenario" element={
          <ProtectedRoute path="/scenario">
            <ScenarioEditor />
          </ProtectedRoute>
        } />
        <Route path="/social-media" element={
          <ProtectedRoute path="/social-media">
            <SocialMedia />
          </ProtectedRoute>
        } />
        <Route path="/production-line" element={
          <ProtectedRoute path="/production-line">
            <ProductionLine />
          </ProtectedRoute>
        } />
        <Route path="/settings" element={
          <ProtectedRoute path="/settings">
            <Settings />
          </ProtectedRoute>
        } />
        <Route path="/control-panel" element={
          <AdminRoute>
            <ControlPanel />
          </AdminRoute>
        } />
        <Route path="/" element={<Navigate to="/login" />} />
      </Routes>
    </Suspense>
  );
}

function App() {
  return (
    <div className="App min-h-screen bg-[#F8F5EF] dark:bg-[#171112] transition-colors duration-200">
      <BrowserRouter>
        <ThemeProvider>
          <AuthProvider>
            <SidebarProvider>
              {/* Offline Detection - Shows overlay when no internet */}
              <OfflineDetector />

              <AppRoutes />
              <Toaster />
            </SidebarProvider>
          </AuthProvider>
        </ThemeProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
